import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Chip,
  DialogActions,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import clsx from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { isEqual } from 'lodash';
import { ArrowLeftRight, Check, CircleStop, RefreshCw, Trash2, X } from 'lucide-react';
import {
  AlineButton,
  AlineDialog,
  AlineTooltip,
  CenteredLoader,
  Spacer,
} from 'src/components/generic';
import { DriveIcon } from 'src/components/icons/DriveIcon';
import { FolderSyncedIcon } from 'src/components/icons/FolderSyncedIcon';
import { FolderSyncingIcon } from 'src/components/icons/FolderSyncingIcon';
import { NoSyncJobsIcon } from 'src/components/icons/NoSyncJobsIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { Apollo } from 'src/gql/generated';
import { useDeleteFolderSyncJobs } from 'src/hooks/folder-sync';
import { appColors } from 'src/theme';
import { FolderSyncJobLabelList } from 'src/workflow/labels';
import { makeStyles } from 'tss-react/mui';

import { FailedFilesChip } from './FailedFilesDialog';
import { getMimeTypeLabels } from './file-type-presets';
import { FileStorageSyncDialog } from './FileStorageSyncDialog';
import { FileTypeFilterChip, FileTypeSelector } from './FileTypeSelector';
import type { FileStorageConnection } from './IntegrationRow';
import { SyncingIndicator } from './SyncingIndicator';
import type { FolderSyncJobSubset } from './useFolderSyncJobs';
import {
  getFolderSyncJobFileIds,
  getFolderSyncJobTitle,
  useFolderSyncJobs,
  useFolderSyncStructure,
} from './useFolderSyncJobs';

export const DROPBOX_FILE_TYPE_FILTER_WARNING =
  "Dropbox doesn't report file types. File-type filters are matched by filename extension and verified during import. Mismatched extensions may cause files to be skipped or fail unexpectedly.";

const STATE_TRANSITION_MS = 180;

const UNLINK_BUTTON_SX = {
  '&:hover': { color: 'error.main' },
  color: 'text.secondary',
  height: 24,
  p: 0,
  width: 24,
} satisfies SxProps<Theme>;

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
  containerWithSyncContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  emptyState: {
    alignItems: 'center',
    color: theme.appColors.secondaryType,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    textAlign: 'center',
  },
  jobCard: {
    '&:hover': {
      backgroundColor: theme.appColors.background,
    },
    backgroundColor: theme.palette.common.white,
    transition: theme.transitions.create('background-color', { duration: STATE_TRANSITION_MS }),
  },
  jobTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    transition: theme.transitions.create('color', { duration: STATE_TRANSITION_MS }),
  },
  wrapper: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

export function FolderSyncJobsList({
  connections,
  integration,
  state,
  stateLoading = false,
}: FolderSyncJobsListProps): JSX.Element | null {
  if (!stateLoading && state !== Apollo.ApiDeckConnectionState.Callable) return null;

  return (
    <FolderSyncJobBody
      connections={connections}
      integration={integration}
      stateLoading={stateLoading}
    />
  );
}

function FolderSyncJobBody({
  connections,
  integration,
  stateLoading,
}: {
  connections: FileStorageConnection[];
  integration: Apollo.FileStorageIntegration;
  stateLoading: boolean;
}): JSX.Element | null {
  const { classes } = useStyles();
  const res = useFolderSyncJobs(integration);
  const { setStructure, structure } = useFolderSyncStructure(integration);
  const folderSyncFolderIds = Object.values(res.grouped).flatMap(group =>
    group.jobs.map(job => job.folderId)
  );

  useEffect(() => {
    // A failed fetch must not overwrite the last known structure with an empty list.
    if (!res.data) return;
    setStructure(
      Object.entries(res.grouped).map(([driveId, { driveName, jobs }]) => ({
        driveId,
        driveName,
        jobs: jobs.map(job => ({ id: job.id, title: getFolderSyncJobTitle(job) })),
      }))
    );
  }, [res.data, res.grouped, setStructure]);

  // Also covers a failed jobs fetch: the cached layout beats a wrong empty state.
  const showStructure = (stateLoading || !res.data) && structure.length > 0;
  if (stateLoading && !showStructure) return null;

  /**
   * The picker excludes folders that already sync using the live jobs, so it must not open
   * before they are known: with an empty list every folder looks available and a second job
   * can be created for a folder that is already syncing. After a failed fetch it stays hidden.
   */
  const jobsKnown = Boolean(res.data);
  const showFooterSkeleton = stateLoading || res.initialLoading;

  const drives: SyncJobDrive[] = showStructure
    ? structure.map(drive => ({
        ...drive,
        jobs: drive.jobs.map(job => ({ ...job, job: null })),
        totalFiles: 0,
      }))
    : Object.entries(res.grouped).map(([driveId, group]) => ({
        ...group,
        driveId,
        jobs: group.jobs.map(job => ({ id: job.id, job, title: getFolderSyncJobTitle(job) })),
      }));

  return (
    <div className={clsx(classes.container, drives.length > 0 && classes.containerWithSyncContent)}>
      <FolderSyncJobBodyMain
        drives={drives}
        integration={integration}
        loading={!showStructure && res.initialLoading}
      />
      {showFooterSkeleton && (
        <Skeleton height={36} sx={{ borderRadius: 28 }} variant="rounded" width={150} />
      )}
      {!stateLoading && jobsKnown && (
        <SyncJobFooter
          connections={connections}
          folderSyncFolderIds={folderSyncFolderIds}
          integration={integration}
        />
      )}
    </div>
  );
}

type SyncJobDrive = {
  driveId: string;
  driveName: string;
  // `job` is null while the cached structure stands in for jobs that are still loading.
  jobs: { id: string; job: FolderSyncJobSubset | null; title: string }[];
  totalFiles: number;
};

function FolderSyncJobBodyMain({
  drives,
  integration,
  loading,
}: {
  drives: SyncJobDrive[];
  integration: Apollo.FileStorageIntegration;
  loading: boolean;
}) {
  const { classes } = useStyles();

  if (loading) return <CenteredLoader />;

  if (!drives.length) {
    return (
      <div className={classes.emptyState}>
        <NoSyncJobsIcon />
        <Typography variant="body2">No files selected</Typography>
      </div>
    );
  }

  return (
    <Stack sx={{ gap: 1.5 }}>
      {drives.map(({ driveId, driveName, jobs, totalFiles }) => (
        <div key={driveId} className={classes.wrapper}>
          <SyncJobHeader
            driveName={driveName}
            jobIds={jobs.flatMap(({ job }) => (job ? [job.id] : []))}
            totalFiles={totalFiles}
          />
          {jobs.map(({ id, job, title }) => (
            <SyncJobRow key={id} integration={integration} job={job} title={title} />
          ))}
        </div>
      ))}
    </Stack>
  );
}

interface FolderSyncJobsListProps {
  connections: FileStorageConnection[];
  integration: Apollo.FileStorageIntegration;
  state: Apollo.ApiDeckConnectionState | null;
  stateLoading?: boolean;
}

function SyncJobHeader({
  driveName,
  jobIds,
  totalFiles,
}: {
  driveName: string;
  jobIds: string[];
  totalFiles: number;
}) {
  const [open, setOpen] = useState(false);
  const { deleteFolderSyncJobs } = useDeleteFolderSyncJobs();

  return (
    <>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', borderBottom: 1, borderColor: 'divider', gap: 1, p: 1.5 }}
      >
        <DriveIcon />
        <Typography variant="subtitle1">{driveName}</Typography>
        <Spacer />
        {Boolean(jobIds.length) && (
          <AlineTooltip arrow placement="top" title="Remove all folder syncs for this drive">
            <IconButton size="small" sx={UNLINK_BUTTON_SX} onClick={() => setOpen(true)}>
              <Trash2 size={16} strokeWidth={2} />
            </IconButton>
          </AlineTooltip>
        )}
      </Stack>
      <StopSyncingDialog
        fileCount={totalFiles}
        folderName={driveName}
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={async () => {
          await deleteFolderSyncJobs(jobIds);
          setOpen(false);
        }}
      />
    </>
  );
}

const useSyncJobRowStyles = makeStyles()(theme => ({
  actionSlot: {
    height: 24,
    // The 16px glyph sits 4px inside the 24px button; pull it in so the visible gap matches.
    marginLeft: -4,
    width: 24,
  },
  jobTitleSyncing: {
    color: theme.appColors.iconsTypeMuted,
  },
  progressBar: {
    '& .MuiLinearProgress-bar': {
      borderRadius: 999,
      transition: 'transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    borderRadius: 999,
    height: 6,
    overflow: 'hidden',
    width: 120,
  },
  progressText: {
    fontSize: 12,
  },
  rowContainer: {
    alignItems: 'center',
    columnGap: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    padding: theme.spacing(0.75, 1.5),
  },
  statusStack: {
    '& > *': {
      gridArea: '1 / 1',
    },
    alignItems: 'center',
    display: 'grid',
    height: 28,
  },
}));

/** With `job` null the row keeps its layout and shows skeletons in place of live data. */
function SyncJobRow({
  integration,
  job,
  title,
}: {
  integration: Apollo.FileStorageIntegration;
  job: FolderSyncJobSubset | null;
  title: string;
}) {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const isJobSyncing = Boolean(job && getIsJobSyncing(job));
  const totalFileIds = job ? getJobTotalFileIds(job) : [];
  const { classes: rowClasses } = useSyncJobRowStyles();
  const { deleteFolderSyncJobs } = useDeleteFolderSyncJobs();
  const fileCount = job?.completedIds.length ?? 0;
  const progressText = `${fileCount}/${totalFileIds.length} ${totalFileIds.length === 1 ? 'file' : 'files'} synced`;

  return (
    <>
      <div className={clsx(classes.jobCard, rowClasses.rowContainer)}>
        <SyncJobFolderIcon syncing={isJobSyncing} />
        <Stack sx={{ gap: 0.5, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Typography
              className={clsx(classes.jobTitle, isJobSyncing && rowClasses.jobTitleSyncing)}
            >
              {title}
            </Typography>
            {job && <FolderSyncStatus job={job} />}
            {!job && (
              <div className={rowClasses.statusStack}>
                <Skeleton height={16} variant="text" width={140} />
              </div>
            )}
          </Stack>
          {!job && <Skeleton height={24} sx={{ borderRadius: 12 }} variant="rounded" width={200} />}
          {job && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <FolderSyncJobLabelList
                folderSyncJob={job}
                numDisplayedLabels={4}
                popoverProps={{
                  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                  style: { transform: `translateY(4px)` },
                  transformOrigin: { horizontal: 'left', vertical: 'top' },
                }}
              />
              <AllowedMimeTypesChips
                allowedMimeTypes={job.allowedMimeTypes}
                disabledReason={isJobSyncing ? 'Stop active sync to change file type filters' : ''}
                folderSyncJobId={job.id}
                integration={integration}
              />
              <FailedFilesChip job={job} />
            </Stack>
          )}
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          {!job && <Skeleton height={16} variant="text" width={40} />}
          {job && (
            <Typography
              noWrap
              className={rowClasses.progressText}
              color="textSecondary"
              variant="body2"
            >
              {progressText}
            </Typography>
          )}
          {job && (
            <LinearProgress
              className={rowClasses.progressBar}
              value={getProgressPercentage(job)}
              variant="determinate"
            />
          )}
          <div className={rowClasses.actionSlot}>
            {job && (
              <AlineTooltip arrow placement="top" title="Remove folder sync">
                <IconButton size="small" sx={UNLINK_BUTTON_SX} onClick={() => setOpen(true)}>
                  <X size={16} strokeWidth={2} />
                </IconButton>
              </AlineTooltip>
            )}
          </div>
        </Stack>
      </div>
      {job && (
        <StopSyncingDialog
          fileCount={totalFileIds.length}
          folderName={
            job.folderName === 'root' ? 'All files and folders' : job.folderName || 'Untitled'
          }
          open={open}
          onClose={() => setOpen(false)}
          onConfirm={async () => {
            await deleteFolderSyncJobs([job.id]);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

const useAllowedMimeTypesChipsStyles = makeStyles()(theme => ({
  ghostChip: {
    border: `1px dashed ${theme.appColors.rule}`,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  warningIcon: {
    flexShrink: 0,
    height: 16,
    width: 16,
  },
}));

function AllowedMimeTypesChips({
  allowedMimeTypes,
  disabledReason = '',
  folderSyncJobId,
  integration,
}: {
  allowedMimeTypes: string[];
  folderSyncJobId: string;
  integration: Apollo.FileStorageIntegration;
  disabledReason?: string;
}): JSX.Element {
  const { classes } = useAllowedMimeTypesChipsStyles();
  const [open, setOpen] = useState(false);
  const disabled = Boolean(disabledReason);
  const labels = useMemo(() => getMimeTypeLabels(allowedMimeTypes), [allowedMimeTypes]);

  if (!labels.length)
    return (
      <>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <span>
            <Chip
              className={classes.ghostChip}
              clickable={!disabled}
              disabled={disabled}
              label="Add file type filter +"
              size="small"
              variant="outlined"
              onClick={disabled ? undefined : () => setOpen(true)}
            />
          </span>
        </AlineTooltip>
        <EditFileTypesDialog
          allowedMimeTypes={allowedMimeTypes}
          folderSyncJobId={folderSyncJobId}
          integration={integration}
          open={open}
          onClose={() => setOpen(false)}
        />
      </>
    );

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div className={classes.root}>
          {labels.map(label => (
            <FileTypeFilterChip
              key={label}
              disabled={disabled}
              label={label}
              onClick={!disabled ? () => setOpen(true) : undefined}
              onDelete={!disabled ? () => setOpen(true) : undefined}
            />
          ))}
          <DropboxFileTypeWarningIcon
            className={classes.warningIcon}
            integration={integration}
            show={Boolean(labels.length)}
          />
        </div>
      </AlineTooltip>
      <EditFileTypesDialog
        allowedMimeTypes={allowedMimeTypes}
        folderSyncJobId={folderSyncJobId}
        integration={integration}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

const useEditFileTypesDialogStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
  },
  paper: {
    borderRadius: theme.spacing(1.5),
    maxWidth: 480,
    minWidth: 480,
  },
}));

function DropboxFileTypeWarningIcon({
  className,
  integration,
  show,
}: {
  integration: Apollo.FileStorageIntegration;
  show: boolean;
  className?: string;
}) {
  if (!show || integration !== Apollo.FileStorageIntegration.Dropbox) return null;

  return (
    <AlineTooltip arrow placement="top" title={DROPBOX_FILE_TYPE_FILTER_WARNING}>
      <WarningIcon className={className} />
    </AlineTooltip>
  );
}

function EditFileTypesDialog({
  allowedMimeTypes,
  folderSyncJobId,
  integration,
  onClose,
  open,
}: {
  allowedMimeTypes: string[];
  folderSyncJobId: string;
  integration: Apollo.FileStorageIntegration;
  onClose: () => void;
  open: boolean;
}) {
  const { classes } = useEditFileTypesDialogStyles();
  const [localMimeTypes, setLocalMimeTypes] = useState(allowedMimeTypes);
  const [setAllowedMimeTypes, { loading }] = Apollo.useSetFolderSyncJobAllowedMimeTypesMutation();
  const isDirty = useMemo(() => {
    const sorted = [...localMimeTypes].sort();
    const sortedOriginal = [...allowedMimeTypes].sort();
    return !isEqual(sorted, sortedOriginal);
  }, [localMimeTypes, allowedMimeTypes]);

  const handleSave = useCallback(async () => {
    await setAllowedMimeTypes({
      variables: { allowedMimeTypes: localMimeTypes, folderSyncJobId },
    });
    onClose();
  }, [setAllowedMimeTypes, localMimeTypes, folderSyncJobId, onClose]);

  return (
    <AlineDialog
      TransitionProps={{ onEnter: () => setLocalMimeTypes(allowedMimeTypes) }}
      classes={{ paper: classes.paper }}
      open={open}
      title="Edit file type filters"
      onClose={onClose}
    >
      <div className={classes.content}>
        {integration === Apollo.FileStorageIntegration.Dropbox && (
          <Alert severity="warning">{DROPBOX_FILE_TYPE_FILTER_WARNING}</Alert>
        )}
        <Typography className={classes.inputLabel} variant="body2">
          Choose which file types to sync. Leave empty to sync all files.
        </Typography>
        <FileTypeSelector value={localMimeTypes} onChange={setLocalMimeTypes} />
      </div>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!isDirty}
          loading={loading}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}

function FolderSyncStatus({ job }: { job: FolderSyncJobSubset }): JSX.Element {
  const { classes } = useSyncJobRowStyles();
  const isJobSyncing = getIsJobSyncing(job);
  const [triggerFolderSyncSchedule, { loading: triggeringSchedule }] =
    Apollo.useTriggerFolderSyncScheduleMutation();
  const [stopFolderSyncJob, { loading: stoppingSync }] = Apollo.useStopFolderSyncJobMutation();

  return (
    <div className={classes.statusStack}>
      <Fade unmountOnExit in={isJobSyncing} timeout={STATE_TRANSITION_MS}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <AlineTooltip arrow placement="top" title="Stop sync">
            <div>
              <IconButton
                disabled={stoppingSync}
                size="small"
                sx={UNLINK_BUTTON_SX}
                onClick={async () => {
                  await stopFolderSyncJob({ variables: { folderSyncJobId: job.id } });
                }}
              >
                <CircleStop size={16} strokeWidth={2} />
              </IconButton>
            </div>
          </AlineTooltip>
          <SyncingIndicator />
        </Stack>
      </Fade>
      <Fade unmountOnExit in={!isJobSyncing} timeout={STATE_TRANSITION_MS}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Check
            color={appColors.connectedCheck}
            size={16}
            strokeLinecap="square"
            strokeWidth={2}
            style={{ flexShrink: 0 }}
          />
          <Typography noWrap className={classes.progressText} color="textSecondary" variant="body2">
            {job.syncFinishedAt
              ? `Last synced ${formatDistanceToNow(new Date(job.syncFinishedAt))} ago`
              : 'Sync up to date'}
          </Typography>
          <AlineTooltip
            arrow
            placement="top"
            title={`Next sync scheduled for tomorrow at ${getNextSyncTime()} EDT. Click to sync immediately.`}
          >
            <div>
              <IconButton
                color="primary"
                disabled={triggeringSchedule}
                size="small"
                onClick={async () => {
                  await triggerFolderSyncSchedule({
                    variables: { folderSyncJobId: job.id },
                  });
                }}
              >
                <RefreshCw size={16} strokeWidth={2} />
              </IconButton>
            </div>
          </AlineTooltip>
        </Stack>
      </Fade>
    </div>
  );
}

function SyncJobFolderIcon({ syncing }: { syncing: boolean }) {
  if (syncing) return <FolderSyncingIcon />;

  return <FolderSyncedIcon />;
}

const useSyncJobFooterStyles = makeStyles()(theme => ({
  syncDriveButton: {
    '&&': {
      '& .MuiButton-endIcon': {
        margin: theme.spacing(0, -0.5, 0, 0.5),
      },
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      borderRadius: 28,
      display: 'flex',
      justifyContent: 'center',
      lineHeight: 'normal',
      minWidth: 'auto',
      padding: theme.spacing(1, 2),
    },
  },
}));

function SyncJobFooter({
  connections,
  folderSyncFolderIds,
  integration,
}: {
  connections: FileStorageConnection[];
  folderSyncFolderIds: string[];
  integration: Apollo.FileStorageIntegration;
}) {
  const { classes } = useSyncJobFooterStyles();

  return (
    <Stack sx={{ alignItems: 'flex-start' }}>
      <FileStorageSyncDialog
        buttonProps={{
          buttonClassName: classes.syncDriveButton,
          color: 'primary',
          endIcon: <ArrowLeftRight size={16} strokeWidth={2} />,
          variant: 'outlined',
        }}
        buttonText="Sync New Drive"
        connections={connections}
        folderSyncFolderIds={folderSyncFolderIds}
        integration={integration}
      />
    </Stack>
  );
}

const useStopSyncingDialogStyles = makeStyles()(theme => ({
  dialogActions: {
    padding: theme.spacing(3),
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  dialogContent: {
    padding: theme.spacing(0, 3),
  },
  dialogDescription: {
    marginBottom: theme.spacing(2),
  },
  dialogTitle: {
    padding: theme.spacing(3, 2),
  },
  folderInfoContainer: {
    alignItems: 'center',
    background: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'space-between',
  },
  folderInfoLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  paper: {
    borderRadius: theme.spacing(1.5),
    maxWidth: 640,
    minWidth: 640,
  },
}));

interface StopSyncingDialogProps {
  fileCount: number;
  folderName: string;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
}

export function StopSyncingDialog({
  fileCount,
  folderName,
  onClose,
  onConfirm,
  open,
}: StopSyncingDialogProps) {
  const { classes } = useStopSyncingDialogStyles();

  return (
    <AlineDialog
      classes={{ paper: classes.paper }}
      contentPaddingPx={0}
      headerClassName={classes.dialogTitle}
      open={open}
      title="Are you sure you want to stop syncing?"
      onClose={onClose}
    >
      <div className={classes.dialogContainer}>
        <Divider />
        <div className={classes.dialogContent}>
          <Typography className={classes.dialogDescription} variant="body1">
            Files will remain in place, but updates will no longer sync.
          </Typography>
          <div className={classes.folderInfoContainer}>
            <div className={classes.folderInfoLeft}>
              <FolderSyncedIcon />
              <Typography variant="body1">{folderName}</Typography>
            </div>
            <Typography color="textSecondary" variant="body2">
              {fileCount} files
            </Typography>
          </div>
        </div>
        <Divider />
      </div>
      <DialogActions className={classes.dialogActions}>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton color="primary" variant="contained" onClick={onConfirm}>
          Yes, Stop Syncing
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}

export function getIsJobSyncing(
  job: Pick<Apollo.FolderSyncJob, 'syncStartedAt' | 'syncFinishedAt' | '__typename'>
): boolean {
  return Boolean(job.syncStartedAt && !job.syncFinishedAt);
}

export function getJobTotalFileIds(
  job: Pick<
    Apollo.FolderSyncJob,
    'completedIds' | 'storageIntegrationIds' | 'failedFiles' | '__typename'
  >
) {
  return getFolderSyncJobFileIds(job);
}

function getProgressPercentage(job: FolderSyncJobSubset): number {
  const total = getJobTotalFileIds(job).length;
  if (total) return (job.completedIds.length / total) * 100;
  return getIsJobSyncing(job) ? 0 : 100;
}

/**
 * Renders 6am UTC time in the user's local timezone.
 */
function getNextSyncTime(): string {
  const tomorrow6amUTC = new Date();
  tomorrow6amUTC.setUTCDate(tomorrow6amUTC.getUTCDate() + 1);
  tomorrow6amUTC.setUTCHours(6, 0, 0, 0);

  const localTime = new Date(tomorrow6amUTC.getTime());

  return format(localTime, 'h:mm a');
}
